import { useCustom, useTranslate } from '@refinedev/core';
import { Alert, Button, Space, Spin, Tooltip, Typography } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ImportedPacket } from './ImportedPacket';
import { ImportNotice } from './ImportNotice';
import { draftFromImport, isSingleDraft, type ImportResult } from './importModel';
import {
  TEMPLATE_DETAIL_FIELDS,
  draftFromDetail,
  emptyDraft,
  type TemplateDetail,
} from './model';
import { printPathOf } from './printModel';
import { WorksheetBuilder } from './WorksheetBuilder';
import { WorksheetImportSection } from './WorksheetImportSection';

/**
 * E34 · the Builder's page — `/worksheets/new` (a new template: DefineWorksheetTemplate),
 * `/worksheets/new?from=<templateId>` ("Use this template": the Builder opens on a COPY — the save is
 * the practice's own version 1, `copiedFrom` the original), `/worksheets/<templateId>/edit` (the
 * practice's own template: "Save as a new version" — ReviseWorksheetTemplate). The template is read once
 * (`worksheetTemplate` — the latest version, whole); the draft lives in the Builder until Save (no
 * autosave; leaving with changes warns).
 *
 * block 5b — IMPORT FROM A FILE (a new template only): the header button opens the import section above the
 * editor (`/worksheets/new?import=1` opens with it); ONE document opens here as the unsaved draft with its
 * banner, a packet opens as the document list (ImportedPacket). Leaving discards it — the upload was never kept.
 */
export const WorksheetBuilderPage = () => {
  const t = useTranslate();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { templateId } = useParams();
  const [search] = useSearchParams();
  const from = search.get('from');
  const loadId = templateId ?? from;
  const [importOpen, setImportOpen] = useState(!loadId && search.get('import') === '1');
  const [imported, setImported] = useState<ImportResult | null>(null);
  // Q-ugc-1 A: the import's tick carries to the imported draft's Save — asked once
  const [importRights, setImportRights] = useState(false);

  const { data, isLoading, isError } = useCustom<TemplateDetail>({
    url: '',
    method: 'get',
    errorNotification: false,
    queryOptions: { enabled: Boolean(loadId) },
    meta: {
      operation: 'worksheetTemplate',
      fields: TEMPLATE_DETAIL_FIELDS,
      variables: { templateId: { name: 'templateId', type: 'ID!', value: loadId } },
    },
  });

  if (loadId && isLoading) return <Spin />;
  const detail = data?.data ?? null;
  if (loadId && (isError || !detail)) {
    return <Alert type="error" showIcon message={t('worksheets.builder.notFound')} />;
  }
  const mode = templateId ? 'revise' : 'define';

  const leave = () => navigate('/worksheets');

  // an import's packet: the document list, Save all, Make an intake packet
  if (imported && !isSingleDraft(imported)) {
    return (
      <ImportedPacket
        result={imported}
        rightsConfirmed={importRights}
        onDone={(to) => navigate(to === 'pathways' ? '/pathways' : '/worksheets')}
      />
    );
  }

  // ONE imported document: the same editor, unsaved, with where it came from
  if (imported) {
    const item = imported.drafts[0];
    return (
      <WorksheetBuilder
        key={`import-${imported.importId}`}
        mode="define"
        initial={draftFromImport(item.draft as NonNullable<typeof item.draft>)}
        importedFrom={imported.importedFrom}
        rightsConfirmed={importRights}
        notice={<ImportNotice importedFrom={imported.importedFrom} notes={item.notes ?? []} />}
        onSaved={leave}
        onCancel={leave}
        cancelLabel={t('worksheets.import.draft.discard')}
        heading={
          <Typography.Title level={3} style={{ margin: 0 }}>
            {t('worksheets.builder.titleNew')}
          </Typography.Title>
        }
      />
    );
  }

  const initial = detail
    ? draftFromDetail(detail, templateId ? 'edit' : 'copy')
    : emptyDraft(i18n.languages[0]?.startsWith('ru') ? 'ru' : 'en');
  const canImport = !loadId;

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {canImport && importOpen ? (
        <WorksheetImportSection
          onResult={(result, rights) => {
            setImportRights(rights);
            setImported(result);
          }}
          onClose={() => setImportOpen(false)}
        />
      ) : null}
      <WorksheetBuilder
        key={loadId ?? 'new'}
        mode={mode}
        initial={initial}
        templateId={templateId ?? null}
        onSaved={leave}
        onCancel={leave}
        headerExtra={
          <>
            {canImport && !importOpen ? (
              <Button onClick={() => setImportOpen(true)} data-testid="worksheet-builder-import">
                {t('worksheets.builder.import')}
              </Button>
            ) : null}
            {/* Q-paper-2 A — Print on the template page: the saved latest version; a scored worksheet or a consent
                is completed in the app (the library's line) */}
            {templateId ? (
              <Tooltip title={detail?.paperEligible ? undefined : t('worksheets.library.printNotEligible')}>
                <span>
                  <Button
                    disabled={!detail?.paperEligible}
                    onClick={() => navigate(printPathOf(templateId))}
                    data-testid="worksheet-builder-print"
                  >
                    {t('worksheets.library.print')}
                  </Button>
                </span>
              </Tooltip>
            ) : null}
          </>
        }
        heading={
          <Typography.Title level={3} style={{ margin: 0 }}>
            {t(
              templateId
                ? 'worksheets.builder.titleEdit'
                : from
                ? 'worksheets.builder.titleCopy'
                : 'worksheets.builder.titleNew',
            )}
          </Typography.Title>
        }
      />
    </Space>
  );
};
